/**
 * Sprint AI-UX · Phase 4 — Diplomexa Intelligence output primitives.
 *
 * 10 reusable, navy-and-gold "academy" components that turn AI text
 * output into a premium structured experience.  Designed to be small,
 * composable and animation-light (no childish effects).
 *
 * Exports
 * -------
 *   DiplomexaBrandHeader           — Diplomexa Intelligence kicker + title
 *   DiplomexaScoreRing             — animated radial score (0..100)
 *   DiplomexaInsightBadge          — pill (strength/warn/info/gold)
 *   DiplomexaOutputCard            — base shell w/ gold-edge halo
 *   DiplomexaChecklist             — animated ticking checklist
 *   DiplomexaRecommendationCard    — single recommendation row
 *   DiplomexaStrengthWeaknessGrid  — paired strengths-vs-improvements grid
 *   DiplomexaNextActionsTimeline   — vertical timeline of next actions
 *   DiplomexaMarkdownRenderer      — markdown / plain-text smart renderer
 *   DiplomexaExportToolbar         — Copy / Markdown / PDF / Save buttons
 *
 * Brand rules — strictly enforced:
 *   - Never surface "OpenAI / ChatGPT / Anthropic / Claude / Gemini /
 *     100ms / Zoom / Dyte / Agora" in user-visible copy.
 *   - User-facing AI label is always "Diplomexa Intelligence" (or one of
 *     the approved variants: "Diplomexa Readiness Coach", etc).
 */
import { useEffect, useState, useMemo } from "react";
import {
  Sparkles, Award, ShieldCheck, TrendingUp, AlertTriangle,
  Check, Copy, Download, Save, ExternalLink, ChevronRight, Wallet,
} from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

/* ─────────────────────────── DiplomexaBrandHeader ─────────────────────────── */
export function DiplomexaBrandHeader({
  kicker = "Diplomexa Intelligence",
  title,
  emphasis,
  subtitle,
  icon: Icon = Sparkles,
  testid = "dpx-output-brand-header",
}) {
  return (
    <div data-testid={testid} className="flex items-start gap-3">
      <div className="grid h-12 w-12 flex-shrink-0 place-items-center rounded-2xl bg-gradient-to-br from-[#0A192F] to-[#0F4C81] text-[#D4AF37] shadow-lg">
        <Icon size={18} aria-hidden="true" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[10px] uppercase tracking-[0.32em] font-semibold text-[#D4AF37]">
          {kicker}
        </p>
        <h3
          className="font-serif text-2xl sm:text-3xl text-[#0A192F] leading-tight mt-1"
          style={{ fontFamily: '"Cormorant Garamond", serif' }}
        >
          {title}
          {emphasis ? (
            <>
              {" "}
              <span className="italic text-[#D4AF37]">{emphasis}</span>
            </>
          ) : null}
        </h3>
        {subtitle ? (
          <p className="text-sm text-slate-600 mt-1">{subtitle}</p>
        ) : null}
      </div>
    </div>
  );
}

/* ─────────────────────────── DiplomexaScoreRing ─────────────────────────── */
export function DiplomexaScoreRing({
  score,
  size = 132,
  label = "Overall",
  suffix = "/100",
  testid = "dpx-score-ring",
}) {
  const clamped = Math.max(0, Math.min(100, Number(score) || 0));
  const [animated, setAnimated] = useState(0);

  // Count-up animation — never childish, easing 800ms.
  useEffect(() => {
    let raf, start;
    const duration = 900;
    const step = (ts) => {
      if (!start) start = ts;
      const t = Math.min(1, (ts - start) / duration);
      // easeOutCubic
      const eased = 1 - Math.pow(1 - t, 3);
      setAnimated(Math.round(clamped * eased));
      if (t < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => raf && cancelAnimationFrame(raf);
  }, [clamped]);

  const radius = (size - 16) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (animated / 100) * circumference;
  const tier =
    clamped >= 85 ? "Podium-ready"
    : clamped >= 70 ? "Strong"
    : clamped >= 55 ? "Promising"
    : clamped >= 40 ? "Developing"
    : "Foundations";

  return (
    <div
      data-testid={testid}
      className="inline-flex flex-col items-center"
    >
      <div className="relative" style={{ width: size, height: size }}>
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="-rotate-90">
          <circle
            cx={size / 2} cy={size / 2} r={radius}
            stroke="#E5E7EB" strokeWidth="8" fill="none"
          />
          <circle
            cx={size / 2} cy={size / 2} r={radius}
            stroke="url(#dpx-score-gradient)" strokeWidth="8" fill="none"
            strokeDasharray={circumference}
            strokeDashoffset={offset}
            strokeLinecap="round"
            style={{ transition: "stroke-dashoffset 240ms ease" }}
          />
          <defs>
            <linearGradient id="dpx-score-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#0F4C81" />
              <stop offset="60%" stopColor="#0A192F" />
              <stop offset="100%" stopColor="#D4AF37" />
            </linearGradient>
          </defs>
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span
            data-testid={`${testid}-value`}
            className="font-serif text-3xl text-[#0A192F] leading-none"
            style={{ fontFamily: '"Cormorant Garamond", serif' }}
          >
            {animated}
            <span className="text-base text-slate-500">{suffix}</span>
          </span>
          <span className="text-[9px] uppercase tracking-[0.28em] text-slate-500 mt-1">
            {label}
          </span>
        </div>
      </div>
      <span className="mt-2 px-3 py-0.5 text-[10px] rounded-full bg-[rgba(212,175,55,0.12)] border border-[rgba(212,175,55,0.3)] text-[#7a5d10] uppercase tracking-[0.18em] font-semibold">
        {tier}
      </span>
    </div>
  );
}

/* ─────────────────────────── DiplomexaInsightBadge ─────────────────────────── */
const BADGE_STYLES = {
  strength: { bg: "bg-emerald-50",  border: "border-emerald-200",  text: "text-emerald-800",  icon: ShieldCheck },
  warn:     { bg: "bg-amber-50",    border: "border-amber-200",    text: "text-amber-800",    icon: AlertTriangle },
  info:     { bg: "bg-sky-50",      border: "border-sky-200",      text: "text-sky-800",      icon: Sparkles },
  gold:     { bg: "bg-[#FBF6E8]",   border: "border-[#D4AF37]/40", text: "text-[#7a5d10]",    icon: Award },
};

export function DiplomexaInsightBadge({ tone = "info", children, testid }) {
  const s = BADGE_STYLES[tone] || BADGE_STYLES.info;
  const Icon = s.icon;
  return (
    <span
      data-testid={testid}
      className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 text-[11px] rounded-full border ${s.bg} ${s.border} ${s.text} font-medium`}
    >
      <Icon size={11} aria-hidden="true" /> {children}
    </span>
  );
}

/* ─────────────────────────── DiplomexaOutputCard ─────────────────────────── */
export function DiplomexaOutputCard({ children, testid = "dpx-output-card", className = "" }) {
  return (
    <div
      data-testid={testid}
      className={`relative overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-md ${className}`}
    >
      {/* Top gradient halo */}
      <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-[#0A192F] via-[#0F4C81] to-[#D4AF37]" />
      {/* Faint gold corner glow */}
      <div
        aria-hidden="true"
        className="absolute -top-16 -right-16 w-48 h-48 rounded-full pointer-events-none"
        style={{
          background:
            "radial-gradient(circle at center, rgba(212,175,55,0.16), transparent 70%)",
          filter: "blur(28px)",
        }}
      />
      <div className="relative">{children}</div>
    </div>
  );
}

/* ─────────────────────────── DiplomexaChecklist ─────────────────────────── */
export function DiplomexaChecklist({ items = [], testid = "dpx-checklist" }) {
  if (!items?.length) return null;
  return (
    <ul data-testid={testid} className="space-y-2.5">
      {items.map((item, i) => (
        <li
          key={i}
          className="flex items-start gap-3"
          style={{ animation: `dpx-fade-up 0.45s ease ${i * 60}ms both` }}
        >
          <span className="mt-0.5 flex-shrink-0 w-5 h-5 rounded-full bg-[#D4AF37] grid place-items-center shadow-sm">
            <Check className="w-3 h-3 text-[#0A192F]" strokeWidth={3} aria-hidden="true" />
          </span>
          <span className="text-sm text-slate-700 leading-relaxed flex-1">{item}</span>
        </li>
      ))}
    </ul>
  );
}

/* ─────────────────────────── DiplomexaRecommendationCard ─────────────────────────── */
export function DiplomexaRecommendationCard({ index, title, body, testid }) {
  return (
    <div
      data-testid={testid}
      className="flex gap-3 rounded-xl border border-slate-200 bg-slate-50/60 p-4 hover:border-[#D4AF37]/40 transition-colors"
    >
      <span className="grid h-7 w-7 flex-shrink-0 place-items-center rounded-full bg-[#0A192F] text-[#D4AF37] font-bold text-xs">
        {String(index ?? "·").padStart(2, "0")}
      </span>
      <div className="flex-1">
        {title ? (
          <p className="text-sm font-semibold text-[#0A192F]">{title}</p>
        ) : null}
        <p className={`text-sm text-slate-600 ${title ? "mt-1" : ""}`}>{body}</p>
      </div>
    </div>
  );
}

/* ─────────────────────────── DiplomexaStrengthWeaknessGrid ─────────────────────────── */
export function DiplomexaStrengthWeaknessGrid({
  strengths = [], weaknesses = [], testid = "dpx-strength-weakness-grid",
}) {
  if (!strengths.length && !weaknesses.length) return null;
  return (
    <div data-testid={testid} className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div className="rounded-xl border border-emerald-100 bg-emerald-50/40 p-4">
        <div className="flex items-center gap-2 mb-2">
          <ShieldCheck className="w-4 h-4 text-emerald-700" />
          <p className="text-[10px] uppercase tracking-[0.22em] font-bold text-emerald-800">
            Strongest points
          </p>
        </div>
        <ul className="space-y-2">
          {strengths.length === 0 ? (
            <li className="text-xs text-slate-500 italic">No specific strengths surfaced.</li>
          ) : strengths.map((s, i) => (
            <li key={i} className="text-sm text-slate-700 flex gap-2">
              <span className="text-emerald-600 mt-0.5">•</span> {s}
            </li>
          ))}
        </ul>
      </div>
      <div className="rounded-xl border border-amber-100 bg-amber-50/40 p-4">
        <div className="flex items-center gap-2 mb-2">
          <AlertTriangle className="w-4 h-4 text-amber-700" />
          <p className="text-[10px] uppercase tracking-[0.22em] font-bold text-amber-800">
            Improvement priorities
          </p>
        </div>
        <ul className="space-y-2">
          {weaknesses.length === 0 ? (
            <li className="text-xs text-slate-500 italic">No specific gaps surfaced.</li>
          ) : weaknesses.map((w, i) => (
            <li key={i} className="text-sm text-slate-700 flex gap-2">
              <span className="text-amber-600 mt-0.5">•</span> {w}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

/* ─────────────────────────── DiplomexaNextActionsTimeline ─────────────────────────── */
export function DiplomexaNextActionsTimeline({ actions = [], testid = "dpx-next-actions" }) {
  if (!actions?.length) return null;
  return (
    <ol data-testid={testid} className="relative space-y-4 ml-2">
      <span
        aria-hidden="true"
        className="absolute left-[7px] top-1 bottom-1 w-px bg-gradient-to-b from-[#D4AF37] via-[#0F4C81] to-transparent"
      />
      {actions.map((a, i) => (
        <li
          key={i}
          className="relative pl-7"
          style={{ animation: `dpx-fade-up 0.45s ease ${i * 70}ms both` }}
        >
          <span
            aria-hidden="true"
            className="absolute left-0 top-1.5 w-3.5 h-3.5 rounded-full bg-white border-2 border-[#D4AF37] shadow-sm"
          />
          <p className="text-sm text-slate-700 leading-relaxed">
            {typeof a === "string" ? a : a.text}
          </p>
          {typeof a === "object" && a.day ? (
            <p className="text-[10px] uppercase tracking-[0.18em] text-slate-400 mt-0.5">
              Day {a.day}
            </p>
          ) : null}
        </li>
      ))}
    </ol>
  );
}

/* ─────────────────────────── markdown / text-parsing helpers ─────────────────────────── */
/**
 * Very small, dependency-free markdown-ish renderer.  Detects:
 *   • # / ## / ### headings
 *   • bold (**…**) and italic (*…*)
 *   • bullet lists ("- " or "* ")
 *   • inline code (`…`)
 *
 * Deliberately small — we don't want a 200kB markdown library on the
 * critical path, and Diplomexa Intelligence output is short.
 */
function escapeHtml(s) {
  return String(s)
    .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;").replace(/'/g, "&#039;");
}

function inlineMarkdown(line) {
  let out = escapeHtml(line);
  out = out.replace(/\*\*(.+?)\*\*/g, '<strong class="text-[#0A192F]">$1</strong>');
  out = out.replace(/(^|\s)\*(?!\*)([^*]+?)\*(?=\s|$)/g, '$1<em>$2</em>');
  out = out.replace(/`([^`]+)`/g, '<code class="px-1.5 py-0.5 rounded bg-slate-100 text-[#0A192F] font-mono text-[12px]">$1</code>');
  return out;
}

export function DiplomexaMarkdownRenderer({ source, testid = "dpx-markdown" }) {
  const lines = useMemo(() => String(source || "").split(/\r?\n/), [source]);
  const blocks = [];
  let listBuffer = [];
  const flushList = () => {
    if (listBuffer.length) {
      blocks.push({ type: "ul", items: listBuffer });
      listBuffer = [];
    }
  };
  for (const raw of lines) {
    const line = raw.replace(/\s+$/, "");
    if (!line.trim()) { flushList(); continue; }
    const bulletMatch = line.match(/^\s*[-*]\s+(.*)$/);
    if (bulletMatch) { listBuffer.push(bulletMatch[1]); continue; }
    flushList();
    const h3 = line.match(/^###\s+(.+)$/);
    const h2 = line.match(/^##\s+(.+)$/);
    const h1 = line.match(/^#\s+(.+)$/);
    if (h1) blocks.push({ type: "h1", text: h1[1] });
    else if (h2) blocks.push({ type: "h2", text: h2[1] });
    else if (h3) blocks.push({ type: "h3", text: h3[1] });
    else blocks.push({ type: "p", text: line });
  }
  flushList();

  return (
    <div data-testid={testid} className="space-y-3">
      {blocks.map((b, i) => {
        if (b.type === "h1")
          return <h2 key={i} className="font-serif text-2xl text-[#0A192F] mt-2"
                      style={{ fontFamily: '"Cormorant Garamond", serif' }}
                      dangerouslySetInnerHTML={{ __html: inlineMarkdown(b.text) }} />;
        if (b.type === "h2")
          return (
            <div key={i} className="mt-2">
              <p className="text-[10px] uppercase tracking-[0.28em] font-bold text-[#D4AF37]">
                {b.text.replace(/[*_`]/g, "")}
              </p>
              <div className="h-px bg-slate-200 mt-2" />
            </div>
          );
        if (b.type === "h3")
          return <p key={i} className="text-sm font-semibold text-[#0A192F] mt-2"
                     dangerouslySetInnerHTML={{ __html: inlineMarkdown(b.text) }} />;
        if (b.type === "ul")
          return (
            <ul key={i} className="space-y-1.5 ml-1">
              {b.items.map((it, j) => (
                <li key={j} className="flex gap-2 text-sm text-slate-700">
                  <span className="mt-2 w-1 h-1 rounded-full bg-[#D4AF37] flex-shrink-0" />
                  <span dangerouslySetInnerHTML={{ __html: inlineMarkdown(it) }} />
                </li>
              ))}
            </ul>
          );
        return <p key={i} className="text-sm text-slate-700 leading-relaxed"
                  dangerouslySetInnerHTML={{ __html: inlineMarkdown(b.text) }} />;
      })}
    </div>
  );
}

/* ─────────────────────────── DiplomexaExportToolbar ─────────────────────────── */
export function DiplomexaExportToolbar({
  source,
  filename = "diplomexa-output",
  onSave,
  onExportPDF,            // optional — when omitted, button is a tooltip-disabled placeholder
  onViewLedger,
  diploksUsed,
  testid = "dpx-export-toolbar",
}) {
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(source || "");
      toast.success("Copied to clipboard");
    } catch { toast.error("Couldn't copy"); }
  };
  const handleMarkdown = () => {
    try {
      const blob = new Blob([source || ""], { type: "text/markdown;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `${filename}.md`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Markdown exported");
    } catch { toast.error("Export failed"); }
  };

  return (
    <div
      data-testid={testid}
      className="flex flex-wrap items-center gap-2 border-t border-slate-200 pt-4 mt-4"
    >
      <button
        type="button"
        data-testid={`${testid}-copy`}
        onClick={handleCopy}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-md border border-slate-200 text-slate-700 hover:border-[#D4AF37] hover:text-[#0A192F] transition-colors"
      >
        <Copy size={12} /> Copy
      </button>
      <button
        type="button"
        data-testid={`${testid}-markdown`}
        onClick={handleMarkdown}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-md border border-slate-200 text-slate-700 hover:border-[#D4AF37] hover:text-[#0A192F] transition-colors"
      >
        <Download size={12} /> Export Markdown
      </button>
      <button
        type="button"
        data-testid={`${testid}-pdf`}
        onClick={onExportPDF ? onExportPDF : () => toast("PDF export coming in Phase 5")}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-md border border-slate-200 text-slate-700 hover:border-[#D4AF37] hover:text-[#0A192F] transition-colors"
      >
        <Download size={12} /> Export PDF
      </button>
      {onSave ? (
        <button
          type="button"
          data-testid={`${testid}-save`}
          onClick={onSave}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-md border border-slate-200 text-slate-700 hover:border-[#D4AF37] hover:text-[#0A192F] transition-colors"
        >
          <Save size={12} /> Save to Dashboard
        </button>
      ) : null}
      {typeof diploksUsed === "number" && diploksUsed > 0 ? (
        <button
          type="button"
          data-testid={`${testid}-view-ledger`}
          onClick={onViewLedger ? onViewLedger : () => window.location.assign("/dashboard/diploks/personal/wallet")}
          className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-md bg-[#0A192F] text-[#D4AF37] font-semibold hover:bg-[#0F4C81]"
        >
          <Wallet size={12} /> {diploksUsed} Diploks used · View ledger
        </button>
      ) : null}
    </div>
  );
}

/* ─────────────────────────── Smart text parser for AI output ─────────────────────────── */

/**
 * Parse Diplomexa Intelligence text into a structured "report" object
 * so the renderer can lay it out as premium sections instead of one
 * big block of text.  This is *defensive* — even random markdown
 * lands in a beautiful shape, never raw text.
 *
 *  Heuristics:
 *    - "Overall Score: 78/100" / "Score: 78" / "78/100"        → overall_score
 *    - "## Strengths" / "**Strongest Points**"                 → strengths list
 *    - "## Weaknesses" / "Improvement Priorities" / "Gaps"     → weaknesses
 *    - "## Recommendations" / "Action Items" / "Next Actions"  → recommendations
 *    - everything else stays in ``body`` (rendered via Markdown).
 */
export function parseDiplomexaOutput(text) {
  const src = String(text || "");
  const result = {
    overall_score: null,
    strengths: [],
    weaknesses: [],
    recommendations: [],
    body: src,
  };

  const scoreMatch =
    src.match(/overall[^:]*:\s*(\d{1,3})\s*\/?\s*100/i) ||
    src.match(/score[^:]*:\s*(\d{1,3})\s*\/?\s*100/i) ||
    src.match(/\b(\d{1,3})\s*\/\s*100\b/);
  if (scoreMatch) {
    const n = Number(scoreMatch[1]);
    if (n >= 0 && n <= 100) result.overall_score = n;
  }

  const lines = src.split(/\r?\n/);
  let section = null;
  let bucket = null;
  for (const raw of lines) {
    const line = raw.trim();
    if (!line) continue;

    // Heading detection (## ... or **...** alone on a line)
    const headingMatch =
      line.match(/^##+\s+(.*)$/) || line.match(/^\*\*([^*]+)\*\*$/);
    if (headingMatch) {
      const h = headingMatch[1].toLowerCase();
      if (/strength|highlight|strongest/.test(h)) { section = "strengths"; bucket = result.strengths; continue; }
      if (/weakness|improve|gap|priority|priorities/.test(h)) { section = "weaknesses"; bucket = result.weaknesses; continue; }
      if (/recommend|action|next step|to do|todo|checklist/.test(h)) { section = "recommendations"; bucket = result.recommendations; continue; }
      section = null; bucket = null;
      continue;
    }
    if (section && bucket) {
      const bullet = line.match(/^(?:[-*]|\d+\.)\s+(.*)$/);
      if (bullet) bucket.push(bullet[1].replace(/[*`_]/g, "").trim());
    }
  }

  return result;
}

/* ─────────────────────────── DiplomexaIntelligenceOutputRenderer (orchestrator) ─────────────────────────── */
/**
 * High-level renderer that takes either:
 *   • the new structured shape  { structured: {...}, content, ...}, OR
 *   • the legacy plain-text shape { content: "…markdown…", ... }
 * and produces a premium navy-and-gold experience.
 *
 * Props
 *   output:        backend response from a Diplomexa Intelligence tool
 *   title / emphasis / subtitle: brand header copy
 *   kicker:        kicker label (default "Diplomexa Intelligence")
 *   icon:          lucide icon for the header
 *   diploksUsed:   number → renders the ledger CTA in the toolbar
 *   onSave:        optional save handler
 *   structuredOverride: optional pre-parsed structure (for tests / custom adapters)
 *   testid:        root data-testid (defaults to "dpx-intelligence-output")
 */
export function DiplomexaIntelligenceOutputRenderer({
  output,
  title = "Diplomexa Intelligence",
  emphasis = "report",
  subtitle = "",
  kicker = "Diplomexa Intelligence",
  icon = Sparkles,
  diploksUsed,
  onSave,
  structuredOverride,
  testid = "dpx-intelligence-output",
}) {
  if (!output) return null;
  const text = output.content || "";
  const structured = structuredOverride
    || output.structured
    || parseDiplomexaOutput(text);

  const score = structured.overall_score ?? output.overall_score ?? null;
  const strengths = structured.strengths?.length ? structured.strengths : (output.strengths || []);
  const weaknesses = structured.weaknesses?.length ? structured.weaknesses : (output.weaknesses || []);
  const recommendations = structured.recommendations?.length
    ? structured.recommendations
    : (output.recommendations || []);

  const hasStructure =
    score !== null || strengths.length > 0 || weaknesses.length > 0 || recommendations.length > 0;

  return (
    <DiplomexaOutputCard testid={testid}>
      <div className="px-5 sm:px-6 pt-5 pb-2">
        <DiplomexaBrandHeader
          kicker={kicker}
          title={title}
          emphasis={emphasis}
          subtitle={subtitle || output.subtitle}
          icon={icon}
        />
      </div>

      <div className="px-5 sm:px-6 pb-5">
        {/* Score + insights row */}
        {hasStructure ? (
          <div className="mt-4 grid grid-cols-1 md:grid-cols-12 gap-5 md:gap-6 items-start">
            {score !== null ? (
              <div className="md:col-span-4 flex flex-col items-center md:items-start">
                <DiplomexaScoreRing score={score} label="Overall" />
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {strengths.length > 0 ? (
                    <DiplomexaInsightBadge tone="strength" testid={`${testid}-badge-strength`}>
                      {strengths.length} strengths
                    </DiplomexaInsightBadge>
                  ) : null}
                  {weaknesses.length > 0 ? (
                    <DiplomexaInsightBadge tone="warn" testid={`${testid}-badge-warn`}>
                      {weaknesses.length} priorities
                    </DiplomexaInsightBadge>
                  ) : null}
                  {recommendations.length > 0 ? (
                    <DiplomexaInsightBadge tone="gold" testid={`${testid}-badge-gold`}>
                      {recommendations.length} actions
                    </DiplomexaInsightBadge>
                  ) : null}
                </div>
              </div>
            ) : null}

            <div className={score !== null ? "md:col-span-8" : "md:col-span-12"}>
              <DiplomexaStrengthWeaknessGrid
                strengths={strengths}
                weaknesses={weaknesses}
              />

              {recommendations.length > 0 ? (
                <div className="mt-5">
                  <p className="text-[10px] uppercase tracking-[0.28em] font-bold text-[#D4AF37] mb-3">
                    Next action checklist
                  </p>
                  <DiplomexaChecklist items={recommendations.slice(0, 7)} />
                </div>
              ) : null}
            </div>
          </div>
        ) : null}

        {/* Body */}
        <div
          data-testid="dpx-intelligence-output-body"
          className={`${hasStructure ? "mt-6 pt-5 border-t border-slate-200" : "mt-4"}`}
        >
          <DiplomexaMarkdownRenderer
            source={text || JSON.stringify(output, null, 2)}
            testid={`${testid}-body`}
          />
        </div>

        <DiplomexaExportToolbar
          source={text}
          filename={`diplomexa-${(emphasis || "output").toLowerCase().replace(/\s+/g, "-")}`}
          onSave={onSave}
          diploksUsed={diploksUsed}
          onExportPDF={async () => {
            try {
              toast.loading("Building branded PDF…", { id: "dpx-pdf" });
              const resp = await api.post(
                "/intelligence/export-pdf",
                {
                  title,
                  emphasis,
                  subtitle: subtitle || output.subtitle || "",
                  kicker,
                  body_markdown: text || "",
                  overall_score: score,
                  strengths,
                  weaknesses,
                  recommendations,
                  user_name: output.user_name || "",
                  event_name: output.event_name || "",
                  role: output.role || "",
                  committee: output.committee || "",
                  country: output.country || "",
                },
                { responseType: "blob" },
              );
              const blob = new Blob([resp.data], { type: "application/pdf" });
              const url = URL.createObjectURL(blob);
              const a = document.createElement("a");
              a.href = url;
              a.download = `diplomexa-${(emphasis || "report").toLowerCase().replace(/\s+/g, "-")}.pdf`;
              document.body.appendChild(a); a.click(); a.remove();
              URL.revokeObjectURL(url);
              toast.success("PDF ready — saved to your downloads", { id: "dpx-pdf" });
            } catch (e) {
              toast.error("Couldn't build PDF — please retry", { id: "dpx-pdf" });
            }
          }}
        />
      </div>
    </DiplomexaOutputCard>
  );
}

export default DiplomexaIntelligenceOutputRenderer;
